import React, { Fragment, useContext, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Grid, Button } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";

// Contexts
import CampaignContext from "../../../context/campaign/CampaignContext";
import ParameterContext from "../../../context/parameters/ParameterContext";

// Components
import CheckoutDetails from "./checkout_form/CheckoutDetails";
import TabForm from "./checkout_form/TabForm";

const useStyles = makeStyles(() => ({
  buttonBox: {
    textAlign: "center",
    marginLeft: "0.5rem",
  },
  button: {
    textTransform: "none",
    "&:focus": {
      outline: "none",
    },
  },
  skeleton: {
    borderRadius: "5px",
  },
}));

const Section_05 = () => {
  const classes = useStyles();

  const {
    display_content,
    selected_campaign,
    form_data,
    form_error,
    setFormError,
    selected_checkout,
    checkout_data,
    checkout_error,
    setCheckoutError,
    checkout_loading,
    createCampaign,
    updateCampaign,
  } = useContext(CampaignContext);
  const { checkout_languages } = useContext(ParameterContext);

  const [current_tab, setCurrentTab] = useState("Images");
  const changeTab = (e, tab_name) => setCurrentTab(tab_name);

  const productFieldsValidation = (compare_data) => {
    let changes = false;

    for (let x in form_data) {
      const a = form_data[x];
      const b = compare_data[x];

      if (x === "deals" || x === "presell" || x === "upsell") {
        if ((x === "presell" || x === "upsell") && a.length !== b.length) {
          changes = true;
          break;
        }

        for (let y in a) {
          if (a[y] !== b[y]) {
            changes = true;
            break;
          }
        }
      } else {
        if (a !== b) {
          changes = true;
          break;
        }
      }
    }

    return changes;
  };

  const productFormValidation = () => {
    // Error Checking
    let noError = true;
    for (let key in form_error) {
      const error = form_error[key];

      if (key === "presell" || key === "upsell") {
        for (let x in error) {
          if (error[x]) noError = false;
        }
      } else {
        if (error) noError = false;
      }
    }

    // Field Checking
    let filled = true;
    for (let key in form_data) {
      const value = form_data[key];

      if (key === "presell" || key === "upsell") {
        for (let x in value) {
          if (value[x] === "") {
            filled = false;
            form_error[key][x] = "Field cannot be empty!";
          }
        }
      } else if (key === "store_name" || key === "store_logo") {
      } else if (!["paused_at"].includes(key)) {
        if (value === "" && key !== "super_saver") {
          filled = false;
          form_error[key] = "Field cannot be empty!";
        } else if (value?.super_saver === "") {
          filled = false;
          form_error["super_saver"] = "Field cannot be empty!";
        }
      }
    }

    // Value Checking
    let changes = false;
    if (display_content === "Edit")
      changes = productFieldsValidation(selected_campaign);

    // Valid To Submit
    let valid = false;
    if (noError && filled) {
      valid = true;
    } else {
      setFormError(form_error);
    }

    return {
      pvalid: valid,
      pchanges: changes,
    };
  };

  const checkoutFieldsValidation = (compare_data) => {
    let changes = false;

    const a = checkout_data;
    const b = compare_data;

    // Images
    if (a.images.logo !== b.images.logo) changes = true;

    for (let idx in a.images.banner) {
      if (a.images.banner[idx] !== b.images.banner[idx]) {
        changes = true;
        break;
      }
    }

    // Reviews
    if (a.reviews.length !== b.reviews.length) changes = true;
    else {
      for (let idx in a.reviews) {
        for (let d_idx in a.reviews[idx]) {
          if (a.reviews[idx][d_idx] !== b.reviews[idx][d_idx]) {
            changes = true;
            break;
          }
        }
      }
    }

    return changes;
  };

  const checkoutFormValidation = () => {
    // Error Checking
    let noError = true;
    for (let x in checkout_error) {
      if (checkout_error[x]) noError = false;
    }

    let filled = true;

    if (
      checkout_data.images.logo === "" ||
      checkout_data.images.banner[0] === "" ||
      checkout_data.images.banner[1] === "" ||
      checkout_data.images.banner[2] === ""
    ) {
      filled = false;
      checkout_error.images = "* Images cannot be empty!";
    }

    if (checkout_data.reviews.length === 0) {
      filled = false;
      checkout_error.reviews = "* Reviews cannot be empty!";
    }

    // Value Checking
    let changes = false;
    if (selected_checkout)
      changes = checkoutFieldsValidation(selected_checkout);

    // Valid To Submit
    let valid = false;
    if (noError && filled) valid = true;
    else {
      // Swipe To Error Tab
      if (!checkout_error.images && checkout_error.reviews)
        changeTab("", "Reviews");
      else changeTab("", "Images");

      setCheckoutError(checkout_error);
    }

    return {
      cvalid: valid,
      cchanges: changes,
    };
  };

  const onSave = (e) => {
    const { pvalid, pchanges } = productFormValidation();
    const { cvalid, cchanges } = checkoutFormValidation();

    if (pvalid && cvalid) {
      if (display_content === "Create") createCampaign(cchanges);
      if (display_content === "Edit") updateCampaign(pchanges, cchanges);
    }
  };

  const onCancel = () => {
    // Return To Search Campaign Page
    window.location.replace(`${window.location.origin}/campaign`);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="caption" color="primary">
          Step 5: Checkout Page
        </Typography>
      </Grid>
      {!checkout_loading && checkout_languages ? (
        <Fragment>
          <CheckoutDetails />
          <TabForm current_tab={current_tab} changeTab={changeTab} />
          <Grid item xs={12} container justifyContent="flex-end">
            <Grid item className={classes.buttonBox}>
              <Button
                variant="contained"
                color="secondary"
                className={classes.button}
                onClick={onCancel}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item className={classes.buttonBox}>
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={onSave}
              >
                {display_content === "Edit" ? "Save" : "Create"}
              </Button>
            </Grid>
          </Grid>
        </Fragment>
      ) : (
        <Skeleton
          variant="rect"
          height={600}
          width="100%"
          className={classes.skeleton}
        />
      )}
    </Grid>
  );
};

export default Section_05;
